import {
    paramDsName,
    paramId,
    paramMapping,
    paramMappingName,
    paramMode,
    paramXPathSrc,
} from '../constants/parameterNames';
import {
    typeDataSource,
    typeOption,
    typeXPath,
} from '../constants/parameterTypes';
import { commandResult } from '../helpers/commandResult';
import { getParamValue } from '../helpers/parameter.accessors';
import { getDataSource } from '../helpers/qRules.dataSource.helpers';
import {
    mappingSource,
    validateMappingName,
} from '../helpers/sharePointMappingSource';
import refresher from './RefreshSharePointListItems.refresher';

const MODE_REPORT = 'report';
const MODE_UPDATE = 'update';

const requiredParameters = [];
const optionalParameters = [
    {
        name: paramDsName,
        description: 'Data source name for list data',
        type: typeDataSource,
    },
    {
        name: paramXPathSrc,
        description: 'Path to the repeating node in your SharePoint list',
        type: typeXPath,
        dsParentName: 'dsname',
    },
    {
        name: paramId,
        description: 'XPath to ID field(s) for SharePoint List ID',
        type: typeXPath,
        dsParentName: 'dsname',
    },
    {
        name: paramMapping,
        description: 'Mapping data source name',
        type: typeDataSource,
    },
    {
        name: paramMappingName,
        description:
            'Specify the mapping to refresh, refreshes all mappings in mapping file if not specified.',
    },
    {
        name: paramMode,
        description: `Indicates whether to actually update fields in the forms with new values (${MODE_UPDATE}) or just report on what would be updated (${MODE_REPORT})`,
        type: typeOption,
        options: [
            {
                name: 'Report',
                value: MODE_REPORT,
            },
            {
                name: 'Update',
                value: MODE_UPDATE,
            },
        ],
    },
];
const refreshSharePointListitemsMustIncludeMappingname =
    'Must include /mappingName if using /xpathsrc.';
const description =
    'This command is used in tandem with SubmitToSharePointList to update list items with most recent edits to the list. Supports changes, including deletes. Can be used in either report or update mode. This command, like SubmitToSharePoint, underwent major revisions with qRules v4.2. The command syntax as documented below works with qRules v4.2 and later, and requires the use of the updated mapping form that installs with qRules v4.2. Previous versions of the command will continue to work without any changes. More information can be found on http://www.infopathdev.com/blogs/hilary/archive/2012/05/30/qrules-submittosharepointlist-command-changes-for-v4-2.aspx. Additional information on the structure of the mapping file can be found on http://www.infopathdev.com/blogs/hilary/archive/2012/05/30/qrules-submittosharepointlist-command-general-overview.aspx.';

async function executeAsync({ sharePointAccess, dataSources, parameters }) {
    const mappingDsName = getParamValue(parameters, paramMapping, 'mapping');
    const mappingName = getParamValue(parameters, paramMappingName, '');
    const settings = {
        dsName: getParamValue(parameters, paramDsName),
        idXPath: getParamValue(parameters, paramId, ''),
        mode: getParamValue(parameters, paramMode, MODE_REPORT),
        xPathSrc: getParamValue(parameters, paramXPathSrc, ''),
    };
    const mappingDs = getDataSource(dataSources, mappingDsName);

    settings.isUpdate = settings.mode.toLowerCase() === MODE_UPDATE;

    //if including xpathsrc, must include mapping name
    if (settings.xPathSrc && !mappingName) {
        throw new Error(refreshSharePointListitemsMustIncludeMappingname);
    }

    settings.mappings = mappingSource(mappingDs, mappingName);
    validateMappingName(mappingName, settings.mappings);

    const mainDataSource = getDataSource(dataSources, '');

    const refresherObj = refresher(mainDataSource, sharePointAccess);
    const baseNodes = [mainDataSource];

    for (const mapping of settings.mappings.topLevelMappings) {
        await refresherObj.refreshSharePointListItems(
            settings,
            mapping,
            baseNodes,
        );
    }

    return commandResult(
        refresherObj.refreshErrors.length === 0,
        refresherObj.listRefreshReport.join(';'),
        refresherObj.refreshErrors.join(' | '),
    );
}

const RefreshSharePointListItems = {
    description,
    executeAsync,
    optionalParameters,
    requiredParameters,
};

export default RefreshSharePointListItems;
